import {
  TolgeeProvider as TolgeeProviderBase,
  useTolgee,
  useTranslate,
  type TranslateOptions,
  type TranslationKey,
} from '@tolgee/react'
import {
  createContext,
  createElement,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
  type ReactElement,
} from 'react'
import { AppState, Platform, Text } from 'react-native'
import { refreshTranslationLanguage } from './cache'
import { createTolgeeInstance, DEFAULT_LANGUAGE, type TolgeeConfig } from './core'
import type { Language, Namespace } from './generated/i18n-meta'
import { getPersistedLanguage } from './languageStorage'
import { type SelectorFunction, type TranslateFunction, type UseTranslationsResult } from './types'
import { extractKeyFromSelector, parseNamespacedKey } from './utils'

declare const __DEV__: boolean | undefined

export function useTranslations<NS extends Namespace | readonly Namespace[] = 'common'>(
  namespaceOrNamespaces?: NS
): UseTranslationsResult<NS> {
  const { t: tolgeeT } = useTranslate()

  const namespaces = namespaceOrNamespaces ?? ('common' as NS)
  const primaryNamespace: Namespace = Array.isArray(namespaces) ? namespaces[0] : namespaces

  const t = useCallback(
    (selectorOrKey: SelectorFunction<NS> | string, options?: TranslateOptions): string => {
      let fullKey: string

      if (typeof selectorOrKey === 'function') {
        fullKey = extractKeyFromSelector(
          selectorOrKey as ($: object) => unknown,
          namespaces as Namespace | readonly Namespace[]
        )
      } else if (selectorOrKey.includes(':')) {
        fullKey = selectorOrKey
      } else {
        fullKey = `${primaryNamespace}:${selectorOrKey}`
      }

      const { ns, key } = parseNamespacedKey(fullKey)
      const result = tolgeeT(key as TranslationKey, { ...options, ns })

      if (__DEV__ && result === key) {
        console.warn(
          `[i18n] Missing translation for key "${key}" in namespace "${ns}". ` +
            `Make sure the namespace is loaded and the key exists.`
        )
      }

      return result
    },
    [tolgeeT, namespaces, primaryNamespace]
  ) as TranslateFunction<NS>

  return { t }
}

export type TolgeeProviderProps = {
  children: ReactNode
  config: Omit<TolgeeConfig, 'initialLanguage'>
}

type TranslationCacheController = {
  refreshCurrentLanguage: () => Promise<void>
}

const TranslationCacheControllerContext = createContext<TranslationCacheController | null>(null)

export function TolgeeProvider({ children, config }: TolgeeProviderProps) {
  const tolgee = useMemo(
    () =>
      createTolgeeInstance({
        apiUrl: config.apiUrl,
        apiKey: config.apiKey,
        cdnUrl: config.cdnUrl,
        cacheKey: config.cacheKey,
      }),
    [config.apiKey, config.apiUrl, config.cacheKey, config.cdnUrl]
  )

  const refreshCurrentLanguage = useCallback(async () => {
    const currentLanguage = (tolgee.getLanguage() ?? DEFAULT_LANGUAGE) as Language
    const changed = await refreshTranslationLanguage(
      currentLanguage,
      config.cdnUrl,
      config.cacheKey
    )

    if (!changed) {
      return
    }

    await tolgee.loadRecords(tolgee.getRequiredDescriptors(currentLanguage), {
      useCache: false,
    })
  }, [config.cacheKey, config.cdnUrl, tolgee])

  const translationCacheController = useMemo(
    () => ({
      refreshCurrentLanguage,
    }),
    [refreshCurrentLanguage]
  )

  useLayoutEffect(() => {
    async function bootstrapTolgee() {
      const persistedLanguage = await getPersistedLanguage()
      const initialLanguage = persistedLanguage ?? DEFAULT_LANGUAGE
      await refreshTranslationLanguage(initialLanguage, config.cdnUrl, config.cacheKey)

      try {
        if (persistedLanguage && persistedLanguage !== tolgee.getLanguage()) {
          await tolgee.changeLanguage(persistedLanguage)
        }

        await tolgee.loadRequired({ language: initialLanguage })
      } catch (error) {
        if (__DEV__) {
          console.error('Error loading translations:', error)
        }
      }
    }

    void bootstrapTolgee()
  }, [config.cacheKey, config.cdnUrl, tolgee])

  return createElement(
    TranslationCacheControllerContext.Provider,
    { value: translationCacheController },
    createElement(TolgeeProviderBase, { tolgee, options: { useSuspense: false } }, children)
  )
}

export function useRefreshTranslationCache(): () => Promise<void> {
  const controller = useContext(TranslationCacheControllerContext)

  if (!controller) {
    throw new Error('useRefreshTranslationCache must be used within TolgeeProvider')
  }

  return controller.refreshCurrentLanguage
}

export function TranslationCacheForegroundRefresh() {
  const refreshTranslationCache = useRefreshTranslationCache()

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const subscription = AppState.addEventListener('change', (status) => {
        if (status === 'active') {
          void refreshTranslationCache()
        }
      })

      return () => {
        subscription.remove()
      }
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    const handleWindowFocus = () => {
      void refreshTranslationCache()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshTranslationCache()
      }
    }

    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refreshTranslationCache])

  return null
}

export function useCurrentLanguage(): Language {
  const tolgee = useTolgee(['language'])
  const [currentLanguage, setCurrentLanguage] = useState<Language>(
    (tolgee.getLanguage() ?? DEFAULT_LANGUAGE) as Language
  )

  useEffect(() => {
    const sub = tolgee.on('language', (languageEvent) => {
      setCurrentLanguage(languageEvent.value as Language)
    })

    return () => {
      sub.unsubscribe()
    }
  }, [tolgee])

  return currentLanguage
}

export type TransProps<NS extends Namespace | readonly Namespace[]> = {
  t: TranslateFunction<NS>
  i18nKey: SelectorFunction<NS>
  components?: Record<
    string,
    ReactElement | ((props: PropsWithChildren<{ key: number }>) => ReactNode)
  >
  values?: Record<string, unknown>
  children?: ReactNode
  noComponentClassName?: string
}

export function Trans<NS extends Namespace | readonly Namespace[]>({
  t,
  i18nKey,
  components,
  noComponentClassName,
}: TransProps<NS>): ReactNode {
  const translated = t(i18nKey as SelectorFunction<NS>)

  if (!components || Object.keys(components).length === 0) {
    return translated
  }

  const parts: ReactNode[] = []
  const regex = /<(\w+)>(.*?)<\/\1>/g
  let lastIndex = 0
  let match: RegExpExecArray | null = null

  while ((match = regex.exec(translated)) !== null) {
    if (match.index > lastIndex) {
      parts.push(translated.slice(lastIndex, match.index))
    }

    const [, tagName, content] = match
    const component = components[tagName]

    if (component) {
      const props =
        typeof component === 'function'
          ? {
              key: match.index,
              children: content,
            }
          : { ...(component as { props: object }).props, key: match.index }

      if (typeof component === 'function') {
        parts.push(component(props))
      } else {
        parts.push(createElement(component.type, props, content))
      }
    } else {
      parts.push(
        // @ts-ignore
        createElement(Text, { key: match.index, className: noComponentClassName }, content)
      )
    }

    lastIndex = regex.lastIndex
  }

  if (lastIndex < translated.length) {
    parts.push(translated.slice(lastIndex))
  }

  return parts
}

export { useTolgee } from '@tolgee/react'
export * from './generated/i18n-meta'
export { getPersistedLanguage } from './languageStorage'
export { type TFunction } from './types'
export { useRouteNamespaces } from './useRouteNamespaces'
